/**
 * Settings singletons — #44574.
 *
 * Ports of:
 *   settings/advisor
 *   settings/company-profile
 *   settings/personal
 *   settings/pulse
 *
 * Live Next pages under excellent/app/settings/{advisor,company-profile,
 * personal,pulse} are UNTOUCHED (D6).
 */

import * as React from "react";
import { createRoute } from "@tanstack/react-router";
import {
  Button,
  FormField,
  Input,
  SettingsPageShell,
  SettingsSection,
  TargetPanel,
  Textarea,
} from "@excellent/ui";
import { apiCall } from "@excellent/ui/api";
import { advisor, type UseCaseGuideDto } from "@excellent/ui/api/contracts/advisor";
import { onboardingRefresh } from "@excellent/ui/api/contracts/onboarding-refresh";
import { rootRoute } from "./__root";

// ── /settings/advisor ───────────────────────────────────────────────────────

function AdvisorSettings() {
  const [business, setBusiness] = React.useState("");
  const [guide, setGuide] = React.useState<UseCaseGuideDto | null>(null);
  const [busy, setBusy] = React.useState<"propose" | "apply" | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [applyMsg, setApplyMsg] = React.useState<string | null>(null);

  async function onPropose() {
    setBusy("propose");
    setError(null);
    setApplyMsg(null);
    try {
      const res = await apiCall(advisor.proposeGuide, {
        body: { business: business.trim() },
      });
      if (res.ok === false) {
        setError(res.error ?? "Could not propose a guide.");
        setGuide(null);
      } else {
        setGuide(res.guide);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function onApply() {
    if (!guide) return;
    setBusy("apply");
    setError(null);
    try {
      const res = await apiCall(advisor.applyGuide, { body: { guide } });
      if (res.ok === false) {
        setError(res.error ?? "Could not apply guide.");
      } else {
        setApplyMsg(
          `Applied — products enabled, ${res.seededLoops?.length ?? 0} loop(s) seeded.`,
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <SettingsPageShell
      title="Use-case advisor"
      description="Describe your business and Excellent tailors itself — recommended products, agentic loops, and quick wins you can apply in one click. Re-describe any time to refresh your guide."
    >
      <SettingsSection
        id="describe"
        title="Describe your business"
        description="One or two sentences. The guide regenerates from this text."
      >
        <Textarea
          value={business}
          onChange={(e) => setBusiness(e.target.value)}
          rows={4}
          placeholder="e.g. We run a boutique design studio for B2B SaaS startups…"
          className="max-w-2xl"
        />
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            type="button"
            disabled={busy !== null || business.trim().length < 3}
            onClick={() => void onPropose()}
          >
            {busy === "propose" ? "Thinking…" : "Generate guide"}
          </Button>
          {guide ? (
            <Button
              type="button"
              variant="outline"
              disabled={busy !== null}
              onClick={() => void onApply()}
            >
              {busy === "apply" ? "Applying…" : "Apply guide"}
            </Button>
          ) : null}
        </div>
        {error ? (
          <p className="mt-2 type-body text-destructive">{error}</p>
        ) : null}
        {applyMsg ? (
          <p className="mt-2 type-body text-muted-foreground">{applyMsg}</p>
        ) : null}
      </SettingsSection>

      {guide ? (
        <SettingsSection
          id="guide"
          title="Your guide"
          description={guide.summary}
        >
          <div className="max-w-2xl space-y-4 type-body">
            <div>
              <p className="font-medium">Industry</p>
              <p className="text-muted-foreground">{guide.industry}</p>
            </div>
            <div>
              <p className="font-medium">Recommended products</p>
              <ul className="mt-1 list-disc pl-5 text-muted-foreground">
                {guide.recommendedProducts.map((p) => (
                  <li key={p.name}>
                    <span className="font-medium text-foreground">{p.name}</span>{" "}
                    — {p.why}
                  </li>
                ))}
              </ul>
            </div>
            {guide.suggestedLoops.length > 0 ? (
              <div>
                <p className="font-medium">Suggested loops</p>
                <ul className="mt-1 list-disc pl-5 text-muted-foreground">
                  {guide.suggestedLoops.map((l) => (
                    <li key={l.title}>
                      <span className="font-medium text-foreground">
                        {l.title}
                      </span>{" "}
                      — replaces {l.replaces}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {guide.quickWins.length > 0 ? (
              <div>
                <p className="font-medium">Quick wins</p>
                <ul className="mt-1 list-disc pl-5 text-muted-foreground">
                  {guide.quickWins.map((w) => (
                    <li key={w.title}>
                      <span className="font-medium text-foreground">
                        {w.title}
                      </span>{" "}
                      — {w.detail}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </SettingsSection>
      ) : null}
    </SettingsPageShell>
  );
}

// ── /settings/company-profile ───────────────────────────────────────────────

function CompanyProfileSettings() {
  const [domain, setDomain] = React.useState("");
  const [since, setSince] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState<string | null>(null);

  async function onRefresh(e: React.FormEvent) {
    e.preventDefault();
    const d = domain.trim();
    if (!d) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await apiCall(onboardingRefresh.run, {
        body: { domain: d },
      });
      setSince(res.started_at);
      setMsg(
        `Enrichment started${res.spawned_count ? ` (${res.spawned_count} worker(s))` : ""} — fields fill in as workers finish.`,
      );
    } catch (err) {
      setMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <SettingsPageShell
      title="Company profile"
      description="The domain captured at onboarding and a button to re-run the enrichment workers that fill workspace fields from the public web."
    >
      <SettingsSection
        id="website"
        title="Website"
        description="Company domain used for enrichment."
      >
        <form
          className="flex max-w-md flex-col gap-3"
          onSubmit={(e) => void onRefresh(e)}
        >
          <FormField label="Domain" htmlFor="company_domain">
            <Input
              id="company_domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="example.com"
            />
          </FormField>
          <Button type="submit" disabled={busy || !domain.trim()}>
            {busy ? "Refreshing…" : "Refresh company profile"}
          </Button>
        </form>
        {msg ? (
          <p className="mt-2 type-body text-muted-foreground">{msg}</p>
        ) : null}
        {since ? (
          <p className="mt-2 type-subtext text-muted-foreground">
            Started at {since}
          </p>
        ) : null}
      </SettingsSection>
    </SettingsPageShell>
  );
}

// ── /settings/pulse ─────────────────────────────────────────────────────────

function PulseTargetsSettings() {
  return (
    <SettingsPageShell
      title="Pulse targets"
      description="Set the benchmark each Health-Wall bar is measured against — and let the wall tell you whether the target is well-set, too loose, or too strict, with a one-click suggestion. Targets are per workspace."
    >
      <TargetPanel mode="customers" />
    </SettingsPageShell>
  );
}

export const settingsAdvisorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/advisor",
  component: AdvisorSettings,
});

export const settingsCompanyProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/company-profile",
  component: CompanyProfileSettings,
});


export const settingsPulseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/pulse",
  component: PulseTargetsSettings,
});
